import { Image as AntdImage } from 'antd';
import clsx from 'clsx';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { user } from '../../../../atoms/user';
import {
  EventDetails,
  logEvent,
} from '../../../../hooks/useClickEventTracking';
import { User } from '../../../../utils/interfaces/User';

interface DataItem {
  id: number;
  type: string;
  value: string | File | object;
  color?: string;
  url?: string;
  altText?: string;
  link?: string;
}

interface RendererProps {
  data: DataItem[];
}

const Renderer: React.FC<RendererProps> = ({ data }) => {
  const [editorLoaded, setEditorLoaded] = useState(false);
  const router = useRouter();
  const { school_id, post_id } = router.query;
  const userData: User | undefined = useRecoilValue(user);

  const editorRef = useRef<any>();
  const { CKEditor, ClassicEditor } = editorRef.current || {};

  useEffect(() => {
    editorRef.current = {
      CKEditor: require('@ckeditor/ckeditor5-react'),
      ClassicEditor: require('@ckeditor/ckeditor5-build-classic'),
    };
    setEditorLoaded(true);
  }, []);

  const formatStringToList = (string: string) => {
    const points = string.split('•').filter((point) => point.trim() !== '');

    const listItems = points.map((point) => {
      const formattedPoint = point.trim();
      return (
        <li
          // className='text-[16px] font-normal    leading-6 md:leading-7 tracking-tight list-disc whitespace-pre-line'
          key={formattedPoint}
        >
          {formattedPoint}
        </li>
      );
    });

    return <ul className='list-disc pl-4 md:pl-8 '>{listItems}</ul>;
  };

  const preprocessHTML = (html: string): string => {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = html;

    const listItems = tempElement.querySelectorAll('li');

    listItems.forEach((li) => {
      for (let i = 0; i < li.childNodes.length; i++) {
        const node = li.childNodes[i];
        if (node instanceof HTMLElement && node.tagName === 'P') {
          while (node.firstChild) {
            li.appendChild(node.firstChild);
          }
          node.parentNode?.removeChild(node);
          i--;
        }
      }
    });

    return tempElement.innerHTML;
  };
  return (
    <div className='flex justify-center mb-5 md:mb-20'>
      <div className='flex flex-col w-full'>
        {data?.map((item, index) => {
          let element: React.ReactNode;
          // let marginBottomClass = ''; // Initialize the class for bottom margin

          // // Check if the next item is a title and add bottom margin if true
          // if (index < data.length - 1 && data[index + 1]?.type === 'title') {
          //   marginBottomClass = ' ';
          // }

          switch (item.type) {
            case 'paragraph':
              element = (
                <p
                  key={item.id}
                  // className={`text-[16px] font-normal a leading-6 md:leading-7 tracking-tight  whitespace-pre-line mt-[0.94em] ${marginBottomClass}`}
                  dangerouslySetInnerHTML={{
                    __html: (item.value as string).replace(
                      /<a\s+(?:[^>]*?\s+)?href="([^"]*)"([^>]*)>(.*?)<\/a>/g,
                      '<a href="$1" target="_blank" style="font-weight: 500;"$2>$3</a>',
                    ),
                  }}
                ></p>
              );
              break;
            case 'title':
              element = (
                <h2
                  key={`${item.id}-title`}
                  style={{
                    color:
                      item.type === 'title' && item.color ? item.color : '',
                  }}
                  // className='xl:text-[26px]   leading-[33.8px] break-normal tracking-normal md:tracking-tighter font-bold whitespace-pre-line   pt-[34px]'
                >
                  {item.value as string}
                </h2>
              );
              break;
            case 'html':
              element = editorLoaded && (
                <div
                  className='customCkeditor customCkeditorSchool border-0'
                  key={`CKEditor-${item.id}`}
                >
                  <CKEditor
                    disabled={true}
                    config={{
                      toolbar: [],
                      readOnly: true,
                      allowedContent: true,
                      extraAllowedContent: 'p(*)[class]',
                      contentsCss: '../../../../styles/ckEditor.scss',
                      styles: [
                        {
                          name: 'Normal',
                          element: 'p',
                        },
                        {
                          name: 'Large',
                          element: 'p',
                        },
                      ],
                      link: {
                        defaultTarget: '_blank',
                        decorators: {
                          openInNewTab: {
                            mode: 'manual',
                            label: 'Open in a new tab',
                            defaultValue: true,
                            attributes: {
                              target: '_blank',
                              rel: 'noopener noreferrer',
                            },
                          },
                        },
                      },
                    }}
                    className='customCkeditor border-0 customCkeditorSchool '
                    data={preprocessHTML(item?.value as string)}
                    editor={ClassicEditor}
                  />
                </div>
              );
              break;
            case 'image':
              element = (
                <div
                  className={clsx(
                    index === 0 && 'mt-5',
                    'pt-[32px] flex justify-center',
                  )}
                  key={`image-${item.id}`}
                >
                  {item.link ? (
                    <a
                      href={item.link}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      <AntdImage
                        src={`${
                          process.env.NEXT_PUBLIC_API_URL_IMAGE_BASE_URL_SCHOOLS
                        }/${item.value as string}`}
                        alt={item?.altText}
                        className='w-full rounded-md'
                      />
                    </a>
                  ) : (
                    <AntdImage
                      src={`${
                        process.env.NEXT_PUBLIC_API_URL_IMAGE_BASE_URL_SCHOOLS
                      }/${item.value as string}`}
                      alt={item?.altText}
                      className='w-full rounded-md'
                    />
                  )}
                </div>
              );
              break;
            case 'anchor':
              element =
                item.value !== undefined ? (
                  <a
                    key={`${item.id}-anchor`}
                    href={item.url || ''}
                    target='_blank'
                    rel='noopener noreferrer'
                    onClick={() => {
                      const eventParams: EventDetails = {
                        event_name: 'advertisment_view',
                        source: 'schools' as string,
                        source_type: 'schools',
                        source_id: school_id as string,
                        sub_source: 'chapter',
                        sub_source_id: post_id as string,
                      };

                      logEvent({
                        eventDetails: eventParams,
                        userData: userData,
                        pathname: router.pathname,
                      });
                    }}
                  >
                    <div className='w-full relative'>
                      <div className=' text-gray-200 mt-[32px] pb-1 text-xs z-30'>
                        Advertisement
                      </div>
                      <div className='flex justify-center w-full'>
                        <Image
                          src={`${
                            process.env
                              .NEXT_PUBLIC_API_URL_IMAGE_BASE_URL_SCHOOLS
                          }/${item.value as string}`}
                          alt='Image'
                          width={500}
                          height={100}
                          style={{
                            width: '100%',
                            height: 'auto',
                            boxShadow:
                              '1px 1px 5px 4px rgba(128, 128, 128, 0.3)',
                          }}
                          className='object-contain rounded-md z-10'
                        />
                      </div>
                    </div>
                  </a>
                ) : null;
              break;
            case 'bullet':
              element = (
                <div key={`${item.id}-bullet`} className=' '>
                  {formatStringToList(item.value as string)}
                </div>
              );
              break;
            default:
              element = null;
          }

          return element;
        })}
      </div>
    </div>
  );
};

export default Renderer;
